import { useMemo } from "react";

import { useLabels } from "~/hooks/useLabels";
import { useBreakpoints } from "~/hooks/useBreakpoints";

import { SheetModal } from "../sheetModal/SheetModal";
import { Box } from "../box/Box";
import { Button } from "../button/Button";
import { Text } from "../text/Text";
import { TagList } from "../tag/TagList";
import { DialogModal } from "../dialogModal/DialogModal";

import "./FeedbackModal.css";

import type { TagProps } from "../tag/Tag";

export interface FeedbackModalProps {
  artwork?: string;
  isOpen?: boolean;
  description: string;
  title: string;
  tags?: TagProps[];
  onPrimaryClick?: () => void;
  onRequestClose?: () => void;
  onSecondaryClick?: () => void;
}

const DEFAULT_LABELS = {
  "feedback.cta.primary": "Give Feedback",
  "feedback.cta.secondary": "Skip",
};

export function FeedbackModal({
  artwork,
  isOpen,
  description,
  tags,
  title,
  onPrimaryClick,
  onRequestClose,
  onSecondaryClick,
}: FeedbackModalProps) {
  const { t } = useLabels({ fallback: DEFAULT_LABELS });

  const breakpoints = useBreakpoints();

  const content = useMemo(() => {
    return (
      <div className="FeedbackModal">
        <div className="FeedbackModal__body">
          {artwork && (
            <img
              className={"FeedbackModal__artwork"}
              src={artwork}
              alt={title}
            />
          )}

          <Text size="3xl" weight="semibold" lineHeight="tight">
            {title}
          </Text>

          <Text color="gray">{description}</Text>

          {tags && <TagList tags={tags} />}
        </div>

        <Box
          direction="column"
          gap={16}
          style={{ marginBlockStart: "var(--size-2)" }}
        >
          {onPrimaryClick && (
            <Button fill onClick={onPrimaryClick}>
              {t("feedback.cta.primary")}
            </Button>
          )}

          {onSecondaryClick && (
            <Button appearance="secondary" fill onClick={onSecondaryClick}>
              {t("feedback.cta.secondary")}
            </Button>
          )}
        </Box>
      </div>
    );
  }, [artwork, description, onPrimaryClick, onSecondaryClick, t, tags, title]);

  return breakpoints.lg ? (
    <DialogModal size="sm" isOpen={isOpen} onRequestClose={onRequestClose}>
      <DialogModal.Body>{content}</DialogModal.Body>
    </DialogModal>
  ) : (
    <SheetModal isOpen={isOpen} onRequestClose={onRequestClose}>
      <SheetModal.Body>{content}</SheetModal.Body>
    </SheetModal>
  );
}
